import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface RevenueSummaryResponse {
  period: string;
  from: string;
  to: string;
  gross_revenue_ils_agorot: number;
  net_revenue_ils_agorot: number;
  vat_collected_ils_agorot: number;
  trailing_30d_ils_agorot: number;
  summary_text: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export const PERIOD_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, highlight }: {
  label: string; value: string | number; sub?: string; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceRevenueOverviewPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'revenue', 'summary', period],
    queryFn: () => apiFetch<RevenueSummaryResponse>(`/finance/revenue/summary?period=${period}`),
    refetchInterval: 30_000,
  });

  const periodBtnBase: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '4px 12px',
    cursor: 'pointer', letterSpacing: '0.04em',
  };

  const periodBtnActive: React.CSSProperties = {
    ...periodBtnBase,
    background: 'rgba(125,211,252,0.12)',
    border: '1px solid rgba(125,211,252,0.35)',
    color: '#7dd3fc',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Revenue' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Revenue
        </h1>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {(['daily', 'weekly', 'monthly'] as const).map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={period === p ? periodBtnActive : periodBtnBase}>
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <>
          <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', marginBottom: 16 }}>
            {fmtDate(data.from)} — {fmtDate(data.to)}
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
            <StatCard
              label="Gross revenue"
              value={fmtIls(data.gross_revenue_ils_agorot)}
              highlight
            />
            <StatCard
              label="Net revenue"
              value={fmtIls(data.net_revenue_ils_agorot)}
              color="var(--omega-state-healthy)"
            />
            <StatCard
              label="VAT collected"
              value={fmtIls(data.vat_collected_ils_agorot)}
            />
            <StatCard
              label="30 recent days"
              value={fmtIls(data.trailing_30d_ils_agorot)}
            />
          </div>
        </>
      )}

      <div style={{ marginTop: 8 }}>
        <Link
          to="/omega/finance/revenue/events"
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
            borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
            textDecoration: 'none', letterSpacing: '0.04em',
          }}
        >
          All revenue events →
        </Link>
      </div>
    </div>
  );
}
